
import React, { useState } from 'react';
import './ProfilePage.css';
import { useApp } from '../../context/AppContext';

interface ProfilePageProps {
  onSignOut?: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onSignOut }) => {
  const { currentUser, orders, products, posts, updateProfile, subscriptionTiers, addShippingAddress } = useApp();
  const [activeTab, setActiveTab] = useState('orders');
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ name: '', street: '', city: '', state: '', zip: '', country: 'USA', isDefault: false });

  const userOrders = orders.filter(o => o.userId === currentUser.id);
  const savedPosts = posts.filter(p => p.savedBy.includes(currentUser.id));
  const wishlistProducts = products.filter(p => currentUser.wishlist.includes(p.id));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'status-delivered';
      case 'shipped': return 'status-shipped';
      case 'processing': return 'status-processing';
      default: return 'status-pending';
    }
  };

  const handleAddAddress = () => {
    if (newAddress.name && newAddress.street && newAddress.city) {
      addShippingAddress(newAddress);
      setShowAddAddress(false);
      setNewAddress({ name: '', street: '', city: '', state: '', zip: '', country: 'USA', isDefault: false });
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <img src={currentUser.avatar} alt={currentUser.displayName} className="profile-avatar" />
        <h1>{currentUser.displayName}</h1>
        <p className="profile-email">{currentUser.email}</p>
        {currentUser.isSubscribed && (
          <div className="subscription-badge">
            <span className="badge-tier">{subscriptionTiers.find(t => t.id === currentUser.subscriptionTier)?.name || 'Subscriber'}</span>
            <span className="badge-expiry">Renews {currentUser.subscriptionExpiry?.toLocaleDateString()}</span>
          </div>
        )}
      </div>

      <div className="profile-tabs">
        <button className={`tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Orders
        </button>
        <button className={`tab ${activeTab === 'saved' ? 'active' : ''}`} onClick={() => setActiveTab('saved')}>
          Saved
        </button>
        <button className={`tab ${activeTab === 'wishlist' ? 'active' : ''}`} onClick={() => setActiveTab('wishlist')}>
          Wishlist
        </button>
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'orders' && (
          <div className="orders-section">
            {userOrders.length === 0 ? (
              <div className="empty-state">
                <p>No orders yet</p>
              </div>
            ) : (
              userOrders.map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div>
                      <span className="order-id">Order #{order.id.slice(-6)}</span>
                      <span className="order-date">{order.createdAt.toLocaleDateString()}</span>
                    </div>
                    <span className={`order-status ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                  <div className="order-items">
                    {order.items.map((item, i) => (
                      <div key={i} className="order-item">
                        <img src={item.productImage} alt={item.productName} />
                        <div className="item-info">
                          <span className="item-name">{item.productName}</span>
                          <span className="item-detail">Size: {item.size} × {item.quantity}</span>
                        </div>
                        <span className="item-price">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="order-footer">
                    <span className="order-total">Total: ${order.total.toFixed(2)}</span>
                    {order.trackingNumber && (
                      <span className="tracking">Tracking: {order.trackingNumber}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'saved' && (
          <div className="saved-section">
            {savedPosts.length === 0 ? (
              <div className="empty-state"><p>No saved content</p></div>
            ) : (
              <div className="saved-grid">
                {savedPosts.map(post => (
                  <div key={post.id} className="saved-item">
                    {post.mediaUrl && <img src={post.mediaUrl} alt="Saved" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'wishlist' && (
          <div className="wishlist-section">
            {wishlistProducts.length === 0 ? (
              <div className="empty-state"><p>No items in wishlist</p></div>
            ) : (
              <div className="wishlist-grid">
                {wishlistProducts.map(p => (
                  <div key={p.id} className="wishlist-item">
                    <img src={p.images[0]} alt={p.name} />
                    <div className="wishlist-info">
                      <span className="name">{p.name}</span>
                      <span className="price">${(p.salePrice || p.price).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="settings-group">
              <h3>Shipping Addresses</h3>
              {currentUser.shippingAddresses.map(addr => (
                <div key={addr.id} className="address-card">
                  <strong>{addr.name}</strong>
                  <p>{addr.street}</p>
                  <p>{addr.city}, {addr.state} {addr.zip}</p>
                  {addr.isDefault && <span className="default-badge">Default</span>}
                </div>
              ))}
              {!showAddAddress ? (
                <button className="add-address-btn" onClick={() => setShowAddAddress(true)}>
                  + Add New Address
                </button>
              ) : (
                <div className="add-address-form">
                  <input placeholder="Address Name (e.g., Home)" value={newAddress.name} onChange={e => setNewAddress({ ...newAddress, name: e.target.value })} />
                  <input placeholder="Street Address" value={newAddress.street} onChange={e => setNewAddress({ ...newAddress, street: e.target.value })} />
                  <div className="form-row">
                    <input placeholder="City" value={newAddress.city} onChange={e => setNewAddress({ ...newAddress, city: e.target.value })} />
                    <input placeholder="State" value={newAddress.state} onChange={e => setNewAddress({ ...newAddress, state: e.target.value })} />
                  </div>
                  <input placeholder="ZIP Code" value={newAddress.zip} onChange={e => setNewAddress({ ...newAddress, zip: e.target.value })} />
                  <div className="form-actions">
                    <button className="save-btn" onClick={handleAddAddress}>Save Address</button>
                    <button className="cancel-btn" onClick={() => setShowAddAddress(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            <div className="settings-group">
              <h3>Notification Preferences</h3>
              <label className="toggle-option">
                <span>New Posts</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.newPosts} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, newPosts: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>Order Updates</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.orderUpdates} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, orderUpdates: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>Messages</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.messages} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, messages: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>New Products</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.newProducts} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, newProducts: e.target.checked } })} />
              </label>
            </div>

            <div className="settings-group">
              <h3>Account</h3>
              {onSignOut && (
                <button className="signout-btn" onClick={onSignOut}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px', marginRight: '8px' }}>
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign Out
                </button>
              )}
              <button className="danger-btn">Cancel Subscription</button>
              <button className="danger-btn outline">Delete Account</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
